import { useTranslation } from "react-i18next";
import { ArrowLeft, BellOff, Building2, CalendarClock, ChevronDown, CheckCircle, Clock, Copy, FileCode, FileDown, Loader2, MoreHorizontal, Pencil, RotateCw, Send, Trash2, XCircle } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/shared/StatusBadge";
import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePageBack } from "@/contexts/PageBackContext";
import { LG_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface OverflowAction {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  destructive?: boolean;
}

export interface InvoiceDetailHeaderInvoice {
  id: string;
  status: string;
  cfeType: string;
  issueDate?: string | null;
  internalNumber?: number | null;
  currency?: string | null;
  scheduledSendAt?: string | null;
  /** Marca this invoice was issued under; null/absent = the legal entity. */
  brandName?: string | null;
  totals?: {
    total?: { currency?: string; amount: number };
    subtotal?: { currency?: string; amount: number };
    taxTotal?: { currency?: string; amount: number };
  } | null;
}

export interface InvoiceDetailHeaderProps {
  invoice: InvoiceDetailHeaderInvoice;
  formatMoney: (currency: string, amount: number) => string;
  onBack: () => void;
  onDownloadPdf: () => void;
  pdfLoading: boolean;
  xmlDownloadAvailable?: boolean;
  onDownloadXml?: () => void;
  xmlLoading?: boolean;
  /** Deel-originated invoices: download the Deel-rendered PDF alongside our CFE. */
  deelPdfAvailable?: boolean;
  onDownloadDeelPdf?: () => void;
  deelPdfLoading?: boolean;
  onReissue?: () => void;
  reissueLoading?: boolean;
  /**
   * Manual "Descartar alerta" affordance for a rejected invoice when
   * the user is NOT planning to reissue. Reissuing already clears the
   * alert automatically — this is the no-reissue escape hatch. Hidden
   * when the row is already dismissed (the page passes undefined).
   */
  onDismissRejection?: () => void;
  dismissingRejection?: boolean;
  onReplicate?: () => void;
  onSendEmail?: () => void;
  emailSending?: boolean;
  emailAlreadySent?: boolean;
  dgiStatus?: string;
  /**
   * Suppress the plain "Rechazada" status banner. Set by the detail page
   * when it renders the richer DGI-rejection alert (code + message) so the
   * two red boxes collapse into a single rejection alert.
   */
  hideRejectionBanner?: boolean;
  onSendDraft?: () => void;
  sendDraftLoading?: boolean;
  onEditDraft?: () => void;
  onDeleteDraft?: () => void;
  deleteDraftLoading?: boolean;
  /** Opens the schedule-send dialog. Only offered while the draft has no schedule yet — once scheduled, the "Programada para..." banner owns rescheduling. */
  onScheduleSend?: () => void;
}

function statusBadgeVariant(kind: "accepted" | "draft" | "rejected"): StatusBadgeVariant {
  switch (kind) {
    case "accepted":
      return "success";
    case "draft":
      return "warning";
    case "rejected":
      return "danger";
  }
}

const BANNER_TONE: Record<"accepted" | "draft" | "rejected", "success" | "warning" | "alert"> = {
  accepted: "success",
  draft: "warning",
  rejected: "alert",
};

export function InvoiceDetailHeader({
  invoice,
  onBack,
  onDownloadPdf,
  pdfLoading,
  xmlDownloadAvailable = false,
  onDownloadXml,
  xmlLoading = false,
  deelPdfAvailable = false,
  onDownloadDeelPdf,
  deelPdfLoading = false,
  onReissue,
  reissueLoading = false,
  onDismissRejection,
  dismissingRejection = false,
  onReplicate,
  onSendEmail,
  emailSending = false,
  emailAlreadySent = false,
  dgiStatus,
  hideRejectionBanner = false,
  onSendDraft,
  sendDraftLoading = false,
  onEditDraft,
  onDeleteDraft,
  deleteDraftLoading = false,
  onScheduleSend,
}: InvoiceDetailHeaderProps): JSX.Element {
  const { t } = useTranslation();
  // Surfaces as the header back arrow on mobile.
  const shownInHeader = usePageBack(onBack);
  const isDesktop = useMediaQuery(LG_QUERY);
  const isDraft = invoice.status === "draft";
  const isRejected = !isDraft && dgiStatus === "rejected";
  const kind: "accepted" | "draft" | "rejected" = isDraft
    ? "draft"
    : isRejected
      ? "rejected"
      : "accepted";
  const showReissue = isRejected && onReissue != null;
  const showDismissRejection = isRejected && onDismissRejection != null;
  const reissueLabel = (() => {
    const cfe = invoice.cfeType ?? "";
    if (cfe === "101" || cfe === "131") return t("invoices.detail.reissueTicket", "Re-issue ticket");
    if (cfe === "102" || cfe === "132") return t("invoices.detail.reissueCreditNote", "Re-issue credit note");
    if (cfe === "103" || cfe === "133") return t("invoices.detail.reissueDebitNote", "Re-issue debit note");
    if (cfe === "111" || cfe === "141") return t("invoices.detail.reissueInvoice", "Re-issue invoice");
    if (cfe === "112" || cfe === "142") return t("invoices.detail.reissueCreditNote", "Re-issue credit note");
    if (cfe === "113" || cfe === "143") return t("invoices.detail.reissueDebitNote", "Re-issue debit note");
    if (cfe === "121") return t("invoices.detail.reissueExportInvoice", "Re-issue export invoice");
    return t("invoices.detail.reissueInvoice", "Re-issue invoice");
  })();

  const primaryNumber =
    invoice.internalNumber != null
      ? String(invoice.internalNumber)
      : isDraft
        ? t("invoices.detail.draft", "Draft")
        : invoice.id.slice(0, 8);
  const cfeLabel = t(`invoices.cfeTypes.${invoice.cfeType}`, invoice.cfeType);
  const titleLead = isDraft
    ? t("invoices.detail.draftTitle", "Draft")
    : `${t("invoices.detail.title", "Invoice")} #${primaryNumber}`;

  const isSentToDgi = dgiStatus === "sent";

  const statusLabel = isDraft
    ? t("invoices.detail.statusCard.draft.title", "Draft")
    : isRejected
      ? t("invoices.list.statusLabel.rejected", "Rechazada")
      : t("invoices.list.statusLabel.sent", "Enviada");

  const StatusIcon =
    kind === "accepted" ? CheckCircle : kind === "rejected" ? XCircle : Clock;

  const showContextSentence = isDraft;
  const contextKey = isDraft
    ? "invoices.detail.header.context.draft"
    : isSentToDgi
      ? "invoices.detail.header.context.sent"
      : "invoices.detail.header.context.pending";

  return (
    <header>
      <div className="fx-phead">
        <div className="fx-phead-text">
          {/* The marca rides in the eyebrow rather than as a separate badge:
              it qualifies WHICH identity issued this comprobante, which is the
              same register as the CFE type. Absent for unbranded invoices, so
              tenants without marcas see the header unchanged. */}
          <span className="fx-eyebrow">
            {invoice.brandName ? `${cfeLabel} · ${invoice.brandName}` : cfeLabel}
          </span>
          <h1>
            {titleLead}{" "}
            <span className="serif">·</span>{" "}
            <span className="serif">{cfeLabel}</span>
          </h1>
        </div>
        <div className="fx-phead-actions">
          {/* Desktop only — on mobile this same action is the header's back
              arrow, and repeating it here just crowds the toolbar. Rendered
              conditionally rather than hidden with a class: .fx-btn sets
              `display` and is UNLAYERED, while Tailwind v4 puts utilities in a
              cascade layer, so `hidden` loses to it no matter the order. */}
          {(isDesktop || !shownInHeader) && (
            <button
              type="button"
              className="fx-btn fx-btn-ghost fx-btn-sm"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("pages.common.back")}
            </button>
          )}

          {/* Primary action(s) — at most two, state-dependent. Everything else
              lives in the overflow menu below so the toolbar stays scannable.
              The split button's wrapper carries `fx-split`: its halves share
              one pill, so they must lift as ONE control on hover, otherwise
              each lifts alone and the seam opens. See icon-motion.css. */}
          {isDraft && onSendDraft && onScheduleSend && !invoice.scheduledSendAt ? (
            <div className="fx-split">
              <button
                type="button"
                className="fx-btn fx-btn-accent fx-btn-sm"
                onClick={onSendDraft}
                disabled={sendDraftLoading}
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              >
                {sendDraftLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
                {t("invoices.detail.sendDraft", "Send invoice")}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="fx-btn fx-btn-accent fx-btn-sm"
                    disabled={sendDraftLoading}
                    aria-label={t("invoices.schedule.scheduleSend", "Programar envío")}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      paddingLeft: 10,
                      paddingRight: 10,
                      borderLeft: "1px solid rgba(255,255,255,0.25)",
                    }}
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onScheduleSend}>
                    <CalendarClock className="h-4 w-4" aria-hidden />
                    {t("invoices.schedule.scheduleSend", "Programar envío")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            isDraft &&
            onSendDraft && (
              <button
                type="button"
                className="fx-btn fx-btn-accent fx-btn-sm"
                onClick={onSendDraft}
                disabled={sendDraftLoading}
              >
                {sendDraftLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
                {t("invoices.detail.sendDraft", "Send invoice")}
              </button>
            )
          )}
          {isDraft && onEditDraft && (
            <button
              type="button"
              className="fx-btn fx-btn-ghost fx-btn-sm"
              onClick={onEditDraft}
            >
              <Pencil className="h-4 w-4" aria-hidden />
              {t("invoices.detail.editDraft", "Edit")}
            </button>
          )}
          {showReissue && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="fx-btn fx-btn-accent fx-btn-sm"
                  onClick={onReissue}
                  disabled={reissueLoading}
                >
                  {reissueLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <RotateCw className="h-4 w-4" aria-hidden />
                  )}
                  {reissueLabel}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[240px]">
                {t("invoices.detail.reissueTooltip", "Creates a new invoice with the same data.")}
              </TooltipContent>
            </Tooltip>
          )}
          {!isDraft && (
            <button
              type="button"
              className="fx-btn fx-btn-primary fx-btn-sm"
              onClick={onDownloadPdf}
              disabled={pdfLoading}
            >
              {pdfLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4" />
              )}
              {t("invoices.detail.downloadPdf", "Download PDF")}
            </button>
          )}
          {!isDraft && !showReissue && onSendEmail && (
            <button
              type="button"
              className="fx-btn fx-btn-ghost fx-btn-sm"
              onClick={onSendEmail}
              disabled={emailSending || emailAlreadySent}
            >
              {emailSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {emailAlreadySent
                ? t("invoices.detail.emailSent", "Email sent")
                : t("invoices.detail.sendEmail", "Send email")}
            </button>
          )}
          {(() => {
            const overflow: OverflowAction[] = [];
            if (isDraft && onDeleteDraft) {
              overflow.push({
                key: "delete",
                label: t("invoices.detail.deleteDraft", "Delete"),
                icon: Trash2,
                onClick: onDeleteDraft,
                loading: deleteDraftLoading,
                destructive: true,
              });
            }
            if (isDraft) {
              overflow.push({
                key: "pdf",
                label: t("invoices.detail.downloadPdf", "Download PDF"),
                icon: FileDown,
                onClick: onDownloadPdf,
                loading: pdfLoading,
              });
            }
            if (showDismissRejection && onDismissRejection) {
              overflow.push({
                key: "dismiss",
                label: t("invoices.detail.dismissRejection", "Descartar alerta"),
                icon: BellOff,
                onClick: onDismissRejection,
                loading: dismissingRejection,
              });
            }
            if (!isDraft && onReplicate != null) {
              overflow.push({
                key: "replicate",
                label: t("invoices.detail.replicateInvoice", "Replicate"),
                icon: Copy,
                onClick: onReplicate,
              });
            }
            if ((isDraft || showReissue) && onSendEmail) {
              overflow.push({
                key: "email",
                label: emailAlreadySent
                  ? t("invoices.detail.emailSent", "Email sent")
                  : t("invoices.detail.sendEmail", "Send email"),
                icon: Send,
                onClick: onSendEmail,
                loading: emailSending,
                disabled: emailAlreadySent,
              });
            }
            overflow.push({
              key: "xml",
              label: t("invoices.detail.downloadXml", "Download XML"),
              icon: FileCode,
              onClick: () => onDownloadXml?.(),
              loading: xmlLoading,
              disabled: !xmlDownloadAvailable || !onDownloadXml,
            });
            if (deelPdfAvailable && onDownloadDeelPdf) {
              overflow.push({
                key: "deel-pdf",
                label: t("invoices.detail.downloadDeelPdf", "PDF de Deel"),
                icon: Building2,
                onClick: onDownloadDeelPdf,
                loading: deelPdfLoading,
              });
            }
            if (overflow.length === 0) return null;
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="fx-btn fx-btn-ghost fx-btn-sm"
                    aria-label={t("invoices.detail.moreActions", "Más acciones")}
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {overflow.map((action) => {
                    const Icon = action.loading ? Loader2 : action.icon;
                    return (
                      <DropdownMenuItem
                        key={action.key}
                        onClick={action.onClick}
                        disabled={action.disabled || action.loading}
                        className={action.destructive ? "text-destructive focus:text-destructive" : undefined}
                      >
                        <Icon className={cn("h-4 w-4", action.loading && "animate-spin")} aria-hidden />
                        {action.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
        </div>
      </div>

      {showContextSentence ? (
        <Collapsible defaultOpen={false} className="fx-panel group">
          <CollapsibleTrigger
            className={cn(
              "flex w-full flex-col gap-2 p-6 text-left transition-colors duration-150 hover:bg-[var(--fx-s-raised-hover)] dark:hover:bg-[var(--fx-s-raised-hover)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-500)] focus-visible:ring-offset-0"
            )}
            aria-expanded={undefined}
          >
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <StatusBadge variant={statusBadgeVariant(kind)}>
                    <StatusIcon className="h-4 w-4 shrink-0 mr-1.5" aria-hidden />
                    {statusLabel}
                  </StatusBadge>
                  <ChevronDown
                    className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                    style={{ color: "var(--fx-ink-500)" }}
                    aria-hidden
                  />
                </div>
                <p className="text-sm" style={{ color: "var(--fx-ink-700)" }}>
                  {t(contextKey)}
                </p>
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t border-[var(--fx-ink-100)] dark:border-[var(--dk-border)] px-6 pb-6 pt-4">
              {isDraft ? (
                <p className="text-sm" style={{ color: "var(--fx-ink-700)" }}>
                  {t(
                    "invoices.detail.nextSteps.draft.body",
                    "This draft has not been sent yet. Review the details and click Send invoice when ready."
                  )}
                </p>
              ) : isSentToDgi ? (
                <p className="text-sm" style={{ color: "var(--fx-ink-700)" }}>
                  {t("invoices.detail.nextSteps.sent.body")}
                </p>
              ) : (
                <p className="text-sm" style={{ color: "var(--fx-ink-700)" }}>
                  {t(
                    "invoices.detail.nextSteps.pending.body",
                    "It will be sent to DGI automatically. No action is required from you."
                  )}
                </p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : kind === "rejected" && hideRejectionBanner ? null : (
        <div className={`fx-banner ${BANNER_TONE[kind]}`} style={{ borderRadius: "var(--fx-r-lg)", marginBottom: 18 }}>
          <StatusIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span>{statusLabel}</span>
        </div>
      )}
    </header>
  );
}
